import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getDefaultAuthenticatedPath, useAuth } from "../context/AuthContext";
import { resolveSelectedMode } from "../utils/appModeFromPath";
import {
  persistExtensionOpenedTab,
  shouldCloseExtensionOpenedTabAfterLogin,
  tryCloseExtensionOpenedTab,
} from "../utils/extensionSession";
import { resolvePostLoginRedirect } from "../services/authLifecycle";
import AppLoadingShell from "./AppLoadingShell";
import { isLoginOAuthCallback, signInWithGoogle } from "../services/auth";
import AuthBrand from "./AuthBrand";
import googleLogo from "../assets/google-logo.svg";
import "../styles/SignIn.css";

const resolveModeForRedirectPath = (redirectPath, fallbackMode) => {
  if (!redirectPath || redirectPath === "/") {
    return fallbackMode;
  }
  try {
    const url = new URL(redirectPath, window.location.origin);
    return resolveSelectedMode(fallbackMode, url.pathname, url.search);
  } catch (_) {
    return fallbackMode;
  }
};

/**
 * Shared sign-in surface parameterized by the enabled product composition.
 *
 * @param {Object} props Authentication presentation from the neutral entrypoint.
 * @returns {React.ReactElement} Product-branded sign-in UI.
 * @example
 * <SignIn presentation={{ brand: { imageSrc: "/logo.svg", alt: "Starmesh" } }} />
 */
const SignIn = ({ presentation = {} }) => {
  const { isAuthenticated, user, selectedMode, desktopAuthError, setDesktopAuthError } =
    useAuth();
  const navigate = useNavigate();
  const tabClosePendingRef = useRef(false);
  const Waitlist = presentation.Waitlist || null;

  useEffect(() => {
    persistExtensionOpenedTab();
    const currentPath = `${window.location.pathname}${window.location.search || ""}`;
    if (
      currentPath !== "/" &&
      currentPath !== "/signin" &&
      currentPath !== "/?code" &&
      !window.location.search.includes("state=login")
    ) {
      sessionStorage.setItem("redirectPath", currentPath);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    if (tabClosePendingRef.current) {
      return;
    }
    const productRedirect = resolvePostLoginRedirect(window.location.search);
    if (productRedirect) {
      navigate(productRedirect, { replace: true });
      return;
    }
    const redirectPath = sessionStorage.getItem("redirectPath");
    const mode = resolveModeForRedirectPath(redirectPath, selectedMode);
    const targetPath =
      redirectPath && redirectPath !== "/"
        ? redirectPath
        : getDefaultAuthenticatedPath(mode);
    persistExtensionOpenedTab();
    if (shouldCloseExtensionOpenedTabAfterLogin()) {
      sessionStorage.removeItem("redirectPath");
      tabClosePendingRef.current = true;
      tryCloseExtensionOpenedTab();
      return;
    }
    sessionStorage.removeItem("redirectPath");
    navigate(targetPath, { replace: true });
  }, [navigate, isAuthenticated, user, selectedMode]);

  const handleSignIn = async () => {
    try {
      setDesktopAuthError?.("");
      persistExtensionOpenedTab();
      await signInWithGoogle();
    } catch (error) {
      console.error("Failed to sign in:", error);
      setDesktopAuthError?.(error?.message || "Failed to start Google sign-in");
    }
  };

  if (isAuthenticated || isLoginOAuthCallback()) {
    return <AppLoadingShell text="Signing you in…" />;
  }

  return (
    <div className="signin-page auth-surface">
      <div className="signin-page__content">
        <AuthBrand
          {...presentation.brand}
          className="signin-page__brand auth-enter auth-enter--1"
        />
        <h1 className="signin-page__headline auth-enter auth-enter--2">
          {presentation.headline}
        </h1>
        {desktopAuthError ? (
          <p
            className="signin-page__error auth-enter auth-enter--2"
            role="alert"
            style={{ color: "#b42318", marginBottom: "1rem", maxWidth: "28rem" }}
          >
            {desktopAuthError}
          </p>
        ) : null}
        <button
          type="button"
          className="google-signin-button auth-enter auth-enter--3"
          onClick={handleSignIn}
        >
          <img src={googleLogo} alt="" aria-hidden="true" />
          <span>Continue with Google</span>
        </button>
        <div className="auth-enter auth-enter--4">
          {Waitlist ? <Waitlist source="web" /> : null}
        </div>
      </div>
      <div className="signin-legal-links">
        <a href={presentation.termsUrl || "https://taskmantis.com/terms.html"} target="_blank" rel="noreferrer">
          Terms of Use
        </a>
        <span className="divider">|</span>
        <a href={presentation.privacyUrl || "https://taskmantis.com/privacy.html"} target="_blank" rel="noreferrer">
          Privacy Policy
        </a>
      </div>
    </div>
  );
};

export default SignIn;
